import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { AuroraBackground } from "../../../components/ui/aurora-background";

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden bg-[#060C18]">
      {/* Aurora animated background */}
      <AuroraBackground
        className="absolute inset-0"
        showRadialGradient={true}
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(21,69,202,0.05) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(21,69,202,0.05) 1px, transparent 1px)`,
          backgroundSize: "72px 72px",
        }}
      />

      {/* Central radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 55% at 50% 45%, rgba(21,69,202,0.12) 0%, transparent 70%)",
        }}
      />

      {/* Corner glows */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-royal-700/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-700/8 blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24 pb-16">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-royal-500/25 bg-royal-500/10 text-royal-300 text-xs font-medium tracking-[0.12em] uppercase mb-10"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-royal-400 animate-pulse inline-block flex-shrink-0" />
          AI-Powered Hair Transplant Verification
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="text-white font-semibold tracking-tight leading-[1.06] mb-7"
          style={{ fontSize: "clamp(2.4rem, 6vw, 4.4rem)" }}
        >
          Verify Every Graft.
          <br />
          <span style={{ color: "#5487FA" }}>Trust Every Result.</span>
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          className="text-slate-400 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed mb-12"
        >
          Graft Counter analyzes post-operation scalp images to estimate graft
          count, map implantation density, and generate professional verification
          reports.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/gc-about")}
            className="group flex items-center gap-2 px-7 py-3.5 bg-royal-600 hover:bg-royal-500 text-white rounded-xl font-medium text-sm transition-all duration-200"
            style={{ boxShadow: "0 0 40px rgba(21,69,202,0.45)" }}
          >
            Get Started
            <ArrowRight
              size={15}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/login-screen")}
            className="flex items-center gap-2 px-7 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/15 hover:border-white/25 rounded-xl font-medium text-sm transition-all duration-200"
          >
            Sign In to Dashboard
          </motion.button>
        </motion.div>

        {/* Key facts strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-20 pt-10 border-t border-white/8 flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16"
        >
          {[
            { label: "Graft Detection", value: "AI-Powered" },
            { label: "Analysis Time", value: "Seconds" },
            { label: "Image Retention", value: "Zero" },
            { label: "Report Format", value: "PDF" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-white font-semibold text-base">{stat.value}</div>
              <div className="text-white/30 text-xs mt-0.5 tracking-widest uppercase font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.35 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white"
      >
        <ChevronDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
